import React from "react";
import styled, { css } from "styled-components";
import Menu from "react-burger-menu/lib/menus/slide";
import { Flex, Box } from "reflexbox";

import * as colors from "../styles/colors";
import { Link, OutboundLink } from "./Link";
import { Icon } from "./Icon";

import menuIcon from "../images/icons/menu-24px.svg";
import closeIcon from "../images/icons/close-icon.svg";
import logo from "../images/logo.png";
import { OutboundLink as GAOutboundLink } from "gatsby-plugin-google-gtag";

import DiscordIcon from "../images/icons/social/discord.inline.svg";
import TwitterIcon from "../images/icons/social/twitter.inline.svg";
import MediumIcon from "../images/icons/social/medium.inline.svg";
import GithubIcon from "../images/icons/social/github.inline.svg";
import InstagramIcon from "../images/icons/social/instagram.inline.svg";

export const socialIcons = {
  discord: (props = {}) => <DiscordIcon {...props} />,
  twitter: (props = {}) => <TwitterIcon {...props} />,
  medium: (props = {}) => <MediumIcon {...props} />,
  github: (props = {}) => <GithubIcon {...props} />,
  instagram: (props = {}) => <InstagramIcon {...props} />
};

const SocialIcons = styled(Flex)`
  display: flex !important;
  flex-direction: row;
`;

const NavContainer = styled.aside`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuItems = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  width: 100%;
  margin: 30px 0;
  text-align: center;
  box-sizing: border-box;
  padding-bottom: 120px;
}
`;

const navbarItemBaseStyle = css`
  height: 48px;
  transition: all 0.3s ease-out;
  margin: 0 8px;
  padding: 0px 20px;
  font-weight: 300;
  border-radius: 8px;
  box-shadow: none;
  min-width: auto;
  text-shadow: 0px 0px #00000000;
  span {
    font-size: 18px;
    background:${({ $accentColor }) => $accentColor};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    -webkit-box-decoration-break: clone;
    text-shadow: 0px 0px #00000000;
  }
  &:hover {
    background: ${colors.backgroundNavItem};
  }
  &:hover span {
    background-size: 0% 100%;
    background-repeat: no-repeat;
    animation: stripes 0.3s ease-in-out 1 forwards;
    -webkit-text-fill-color: transparent;
    background-image: ${({ $accentGradient }) => $accentGradient}
    color: #fff;
    text-decoration: none;
    -webkit-background-clip: text;
    background-clip: text;
  }
  @keyframes stripes {
    to {
      background-size: 100% 100%;
    }
  }
  &.active {
    background: ${colors.backgroundNavItem};
    span {
      color: ${colors.primary};
    }
  }
  &:focus {
    outline: none;
    transition: all 2s ease-out;
    box-shadow: none;
    background: ${colors.backgroundNavItem};
    span {
      ${({ $accentGradient }) => $accentGradient};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      -moz-background-clip: text;
      -moz-text-fill-color: transparent;
    }
  }
`;

const MenuItem = styled(Link)`
  color: ${colors.primary};
  font-size: 18px;
  font-weight: 300;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  margin: 0;
  padding: 17px 0;

  &.active {
    color: ${colors.primary};
    background: ${colors.darkThemeColor};
    border-radius: 8px;
  }

  &:hover {
    color: ${colors.primary};
  }

  &:focus {
    box-shadow: none !important;
  }
`;

const ctaStyle = css`
  font-family: "Eina02-SemiBold", sans-serif !important;
  height: 48px;
  transition: all 0.3s ease-out;
  margin: 0 8px;
  padding: 0px 20px;
  font-weight: 300;
  border-radius: 8px;
  box-shadow: none;
  min-width: auto;
  text-shadow: 0px 0px
  
  padding: 12px 24px;
  font-weight: 400;
  font-size: 18px;
  border-radius: 8px;
  border: 0;
  box-shadow: none;
  background-color: ${({ $ctaBackgroundColor }) =>
    $ctaBackgroundColor ?? colors.primaryButtonColor};
  color: ${({ $ctaTextColor }) =>
    $ctaTextColor ?? colors.whiteLightColor};
  &:hover {
    box-shadow: 0px 0px 15px  ${({ $ctaBackgroundColor }) =>
      $ctaBackgroundColor ?? "rgba(255, 88, 65, 0.8)"};
    transition: box-shadow 0.2s ease-out;
  }
  &:focus,
  &.loading {
    outline: none;
    color: ${({ $ctaTextColor }) => $ctaTextColor ?? colors.light};
    background-color: ${({ $ctaBackgroundColor }) =>
      $ctaBackgroundColor ?? colors.primaryButtonColor};
    box-shadow: none;
  }
`;

const DesktopMenuItem = styled(Link)`
  ${navbarItemBaseStyle}
`;

const DesktopMenuItemCta = styled(Link)`
  ${ctaStyle}
  &.active {
    font-family: "Eina02-SemiBold", sans-serif !important;
    background: ${colors.backgroundNavItem};

    span {
      color: ${colors.primary};
    }
  }

  &.active:hover {
    background: ${colors.backgroundNavItem};
    outline: none;
    box-shadow: none;
  }
  @keyframes stripes {
    to {
      background-size: 100% 100%;
    }
  }
`;

const logoProps = {
  as: "img",
  alignSelf: "flex-start",
  width: ["140px"],
  alt: "Neurosity",
  src: logo
};

export function Navigation({ ctaComponent }) {
  return (
    <NavContainer>{ctaComponent ? ctaComponent : null}</NavContainer>
  );
}

export function HamburgerMenu({ links, socialLinks, productMetadata }) {
  return (
    <Menu
      right
      disableAutoFocus
      pageWrapId={"page-container"}
      outerContainerId={"main-container"}
      customBurgerIcon={<Icon src={menuIcon} alt="Open Menu" />}
      customCrossIcon={<Icon src={closeIcon} alt="Close Menu" />}
      className="mobile-menu"
    >
      <Flex
        {...logoProps}
        style={{ cursor: "pointer", position: "fixed", top: 30 }}
      />
      <MenuItems>
        <>
          {links.map(({ title, type, path }) =>
            type === "href" ? (
              <MenuItem key={title} as={OutboundLink} href={path}>
                {title}
              </MenuItem>
            ) : (
              <MenuItem key={title} to={path} activeClassName="active">
                {title}
              </MenuItem>
            )
          )}
          <MenuItem
            activeClassName="active"
            to={productMetadata?.path}
            title={productMetadata?.cta}
          >
            {productMetadata?.cta}
          </MenuItem>
        </>
      </MenuItems>

      <SocialIcons flexDirection={["row"]} mb={[40]}>
        {socialLinks.links.map(({ id, title, path }, index) => (
          <Box key={id} mx={"10px"}>
            <GAOutboundLink
              href={path}
              target="_blank"
              rel="noopener noreferrer"
              title={title}
              aria-label={title}
              style={{ padding: "6px 0" }}
            >
              {socialIcons?.[title.toLowerCase()]?.({
                fill: colors.primary
              })}
            </GAOutboundLink>
          </Box>
        ))}
      </SocialIcons>
    </Menu>
  );
}

export function DesktopMenu({
  accentColor,
  accentGradient,
  ctaBackgroundColor,
  ctaTextColor,
  links,
  productMetadata,
  cta
}) {
  return (
    <Flex
      as="nav"
      className="desktop-menu"
      alignItems="center"
      mr={"8px"}
    >
      <>
        {links.map(({ title, type, path }) =>
          type === "href" ? (
            <DesktopMenuItem
              key={title}
              as={OutboundLink}
              href={path}
              $accentColor={accentColor}
              $accentGradient={accentGradient}
            >
              {title}
            </DesktopMenuItem>
          ) : (
            <DesktopMenuItem
              key={title}
              to={path}
              activeClassName="active"
              $accentColor={accentColor}
              $accentGradient={accentGradient}
            >
              {title}
            </DesktopMenuItem>
          )
        )}
        {cta && (
          <DesktopMenuItemCta
            to={productMetadata?.path}
            title={productMetadata?.cta}
            more
            activeClassName="active"
            $ctaBackgroundColor={ctaBackgroundColor}
            $ctaTextColor={ctaTextColor}
            arrowColor={ctaTextColor}
          >
            {productMetadata?.cta}
          </DesktopMenuItemCta>
        )}
      </>
    </Flex>
  );
}
